import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  MessageSquare, Plus, Edit2, Trash2, Loader2, X, CheckCircle,
  AlertTriangle, Paperclip, Copy,
} from 'lucide-react';
import AdminLayout from '../components/AdminLayout';
import { apiFetch } from '../lib/api';

// ─── Types ───────────────────────────────────────────────────────────────────

interface CloudwapiTemplate {
  id: number;
  key: string;
  name: string;
  category: string;
  description: string | null;
  message: string;
  mediaUrl: string | null;
  mediaType: string | null;
  isActive: number | boolean;
  createdAt: number;
  updatedAt: number;
  usedBy: Array<{ workflowKey: string; workflowName: string }>;
}

interface FormState {
  key: string;
  name: string;
  category: string;
  description: string;
  message: string;
  mediaUrl: string;
  mediaType: string;
}

// ─── Constants ───────────────────────────────────────────────────────────────

const CATEGORIES = [
  { value: 'all', label: '全部' },
  { value: 'journey', label: '客戶旅程' },
  { value: 'internal', label: '內部通知' },
  { value: 'supplier', label: '供應商' },
  { value: 'partner', label: '合作方' },
  { value: 'general', label: '通用' },
] as const;

type CategoryValue = typeof CATEGORIES[number]['value'];

const CATEGORY_BADGE_CLASS: Record<string, string> = {
  journey:  'bg-purple-100 text-purple-700 border-purple-200 dark:bg-purple-900/30 dark:text-purple-300 dark:border-purple-800',
  internal: 'bg-blue-100 text-blue-700 border-blue-200 dark:bg-blue-900/30 dark:text-blue-300 dark:border-blue-800',
  supplier: 'bg-green-100 text-green-700 border-green-200 dark:bg-green-900/30 dark:text-green-300 dark:border-green-800',
  partner:  'bg-orange-100 text-orange-700 border-orange-200 dark:bg-orange-900/30 dark:text-orange-300 dark:border-orange-800',
  general:  'bg-gray-100 text-gray-700 border-gray-200 dark:bg-gray-700 dark:text-gray-300 dark:border-gray-600',
};

const CATEGORY_LABELS: Record<string, string> = {
  journey: '客戶旅程',
  internal: '內部通知',
  supplier: '供應商',
  partner: '合作方',
  general: '通用',
};

const MEDIA_TYPES = [
  { value: '', label: '無' },
  { value: 'image', label: '圖片' },
  { value: 'video', label: '影片' },
  { value: 'audio', label: '音頻' },
  { value: 'document', label: '文件' },
] as const;

const VARIABLE_HINTS = [
  { token: '{{event.orderNum}}', label: '訂單編號' },
  { token: '{{event.totalPrice}}', label: '訂單總額' },
  { token: '{{customer.name}}', label: '客戶姓名' },
  { token: '{{customer.phone}}', label: '客戶電話' },
  { token: '{{proofUrl}}', label: '付款憑證上傳連結' },
];

const KEY_REGEX = /^[a-z0-9_]+$/;

const defaultForm: FormState = {
  key: '',
  name: '',
  category: 'general',
  description: '',
  message: '',
  mediaUrl: '',
  mediaType: '',
};

// ─── Helpers ─────────────────────────────────────────────────────────────────

function toBool(v: number | boolean): boolean {
  return v === 1 || v === true;
}

function truncate(text: string, maxLen: number): string {
  if (text.length <= maxLen) return text;
  return text.slice(0, maxLen) + '…';
}

// ─── Component ───────────────────────────────────────────────────────────────

const AdminCloudwapiTemplates: React.FC = () => {
  const navigate = useNavigate();
  const token = localStorage.getItem('admin_token');

  const [templates, setTemplates] = useState<CloudwapiTemplate[]>([]);
  const [activeCategory, setActiveCategory] = useState<CategoryValue>('all');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  // Modal state
  const [showModal, setShowModal] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [form, setForm] = useState<FormState>(defaultForm);

  // Delete confirmation state
  const [deleteTarget, setDeleteTarget] = useState<CloudwapiTemplate | null>(null);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  // Copy key feedback
  const [copiedKey, setCopiedKey] = useState<string | null>(null);

  // ─── Data loading ──────────────────────────────────────────────────────

  useEffect(() => {
    if (!token) {
      navigate('/admin');
      return;
    }
    loadTemplates();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, navigate]);

  const loadTemplates = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const url = activeCategory !== 'all'
        ? `/api/public/admin/cloudwapi-templates?category=${activeCategory}`
        : '/api/public/admin/cloudwapi-templates';
      const res = await apiFetch(url);
      const data = await res.json().catch(() => []);
      if (!res.ok) throw new Error(data?.error || '獲取模板失敗');
      setTemplates(Array.isArray(data) ? data : []);
    } catch (e: any) {
      setError(e.message || '獲取模板失敗');
    } finally {
      setLoading(false);
    }
  }, [activeCategory]);

  useEffect(() => {
    if (token) loadTemplates();
  }, [token, loadTemplates]);

  // ─── Filtered & grouped templates ──────────────────────────────────────

  const filtered = useMemo(() => templates, [templates]);

  const grouped = useMemo(() => {
    const map = new Map<string, CloudwapiTemplate[]>();
    for (const t of filtered) {
      const cat = t.category || 'general';
      if (!map.has(cat)) map.set(cat, []);
      map.get(cat)!.push(t);
    }
    return map;
  }, [filtered]);

  // ─── Modal handlers ────────────────────────────────────────────────────

  const openCreate = () => {
    setEditingId(null);
    setForm({ ...defaultForm, category: activeCategory === 'all' ? 'general' : activeCategory });
    setShowModal(true);
  };

  const openEdit = (tpl: CloudwapiTemplate) => {
    setEditingId(tpl.id);
    setForm({
      key: tpl.key,
      name: tpl.name,
      category: tpl.category || 'general',
      description: tpl.description || '',
      message: tpl.message,
      mediaUrl: tpl.mediaUrl || '',
      mediaType: tpl.mediaType || '',
    });
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setEditingId(null);
    setForm(defaultForm);
  };

  // ─── Form submission ───────────────────────────────────────────────────

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!editingId && !form.key.trim()) {
      setError('請填寫模板代碼');
      return;
    }
    if (!editingId && !KEY_REGEX.test(form.key.trim())) {
      setError('模板代碼只能包含小寫字母、數字同底線');
      return;
    }
    if (!form.name.trim()) {
      setError('請填寫模板名稱');
      return;
    }
    if (!form.message.trim()) {
      setError('請填寫訊息內容');
      return;
    }
    if (form.mediaUrl.trim() && !form.mediaType) {
      setError('設定媒體連結時請選擇媒體類型');
      return;
    }

    setSaving(true);
    setSuccess(null);

    try {
      const payload: Record<string, any> = {
        name: form.name.trim(),
        category: form.category,
        description: form.description.trim() || null,
        message: form.message.trim(),
        mediaUrl: form.mediaUrl.trim() || null,
        mediaType: form.mediaType || null,
      };
      if (!editingId) {
        payload.key = form.key.trim();
      }

      const url = editingId
        ? `/api/public/admin/cloudwapi-templates/${editingId}`
        : '/api/public/admin/cloudwapi-templates';
      const method = editingId ? 'PUT' : 'POST';

      const res = await apiFetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || '儲存失敗');

      setSuccess(editingId ? '模板已更新' : '模板已建立');
      closeModal();
      loadTemplates();
    } catch (e: any) {
      setError(e.message || '儲存失敗');
    } finally {
      setSaving(false);
      setTimeout(() => setSuccess(null), 3000);
    }
  };

  // ─── Delete ────────────────────────────────────────────────────────────

  const openDeleteConfirm = (tpl: CloudwapiTemplate) => {
    if (tpl.usedBy && tpl.usedBy.length > 0) {
      const names = tpl.usedBy.map(w => w.workflowName).join('、');
      setDeleteError(`無法刪除：模板正在被以下工作流程使用：${names}`);
      return;
    }
    setDeleteTarget(tpl);
    setDeleteError(null);
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    setSaving(true);
    setError(null);
    try {
      const res = await apiFetch(`/api/public/admin/cloudwapi-templates/${deleteTarget.id}`, { method: 'DELETE' });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        if (res.status === 409 && Array.isArray(data.usedBy)) {
          throw new Error(`無法刪除：模板正在被以下工作流程使用：${data.usedBy.join('、')}`);
        }
        throw new Error(data.error || '刪除失敗');
      }
      setSuccess('模板已刪除');
      setDeleteTarget(null);
      loadTemplates();
    } catch (e: any) {
      setError(e.message || '刪除失敗');
    } finally {
      setSaving(false);
      setTimeout(() => setSuccess(null), 3000);
    }
  };

  // ─── Copy key ──────────────────────────────────────────────────────────

  const copyKey = (key: string) => {
    navigator.clipboard.writeText(key).then(() => {
      setCopiedKey(key);
      setTimeout(() => setCopiedKey(null), 1500);
    }).catch(() => {});
  };

  // ─── Logout ────────────────────────────────────────────────────────────

  const handleLogout = () => {
    if (confirm('確定要登出嗎？')) {
      localStorage.removeItem('admin_token');
      localStorage.removeItem('admin_user');
      navigate('/admin');
    }
  };

  // ─── Insert variable into textarea ─────────────────────────────────────

  const insertVariable = (token: string) => {
    setForm(f => ({ ...f, message: f.message + token }));
  };

  // ─── Render ────────────────────────────────────────────────────────────

  return (
    <AdminLayout currentPage="cloudwapi-templates" onLogout={handleLogout}>
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-800 dark:text-white flex items-center gap-2">
          <MessageSquare className="w-6 h-6 text-orange-600" />
          CloudWAPI 訊息模板
        </h1>
        <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
          管理唔需要 Meta 審批嘅 WhatsApp 訊息模板，適用於合作方通知、供應商通訊同內部訊息
        </p>
      </div>

      {/* Flash messages */}
      {error && (
        <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg text-red-700 flex justify-between items-center dark:bg-red-900/20 dark:border-red-800 dark:text-red-300">
          <span>{error}</span>
          <button onClick={() => setError(null)} className="text-red-700 hover:text-red-900 dark:text-red-300 dark:hover:text-red-100">
            <X className="w-5 h-5" />
          </button>
        </div>
      )}
      {success && (
        <div className="mb-4 p-4 bg-green-50 border border-green-200 rounded-lg text-green-700 flex justify-between items-center dark:bg-green-900/20 dark:border-green-800 dark:text-green-300">
          <span className="flex items-center gap-1"><CheckCircle className="w-4 h-4" /> {success}</span>
          <button onClick={() => setSuccess(null)} className="text-green-700 hover:text-green-900 dark:text-green-300 dark:hover:text-green-100">
            <X className="w-5 h-5" />
          </button>
        </div>
      )}
      {deleteError && (
        <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg text-red-700 flex justify-between items-center dark:bg-red-900/20 dark:border-red-800 dark:text-red-300">
          <span className="flex items-center gap-1"><AlertTriangle className="w-4 h-4 flex-shrink-0" /> {deleteError}</span>
          <button onClick={() => setDeleteError(null)} className="text-red-700 hover:text-red-900 dark:text-red-300 dark:hover:text-red-100">
            <X className="w-5 h-5" />
          </button>
        </div>
      )}

      {/* Category Tabs + Create button */}
      <div className="mb-6 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-1 inline-flex flex-wrap">
          {CATEGORIES.map((cat) => (
            <button
              key={cat.value}
              onClick={() => setActiveCategory(cat.value)}
              className={`px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                activeCategory === cat.value
                  ? 'bg-orange-600 text-white'
                  : 'text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700'
              }`}
            >
              {cat.label}
            </button>
          ))}
        </div>
        <button
          onClick={openCreate}
          className="inline-flex items-center gap-1 bg-orange-600 text-white px-4 py-2 rounded-lg text-sm hover:bg-orange-700 transition-colors"
        >
          <Plus className="w-4 h-4" /> 新增模板
        </button>
      </div>

      {/* Template List */}
      {loading ? (
        <div className="flex items-center justify-center py-20 text-gray-400">
          <Loader2 className="w-6 h-6 animate-spin mr-2" /> 加載中...
        </div>
      ) : filtered.length === 0 ? (
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow border border-gray-200 dark:border-gray-700 p-12 text-center">
          <MessageSquare className="w-12 h-12 text-gray-300 dark:text-gray-600 mx-auto mb-4" />
          <p className="text-gray-500 dark:text-gray-400 text-lg mb-2">暫無模板</p>
          <p className="text-gray-400 dark:text-gray-500 text-sm mb-4">點擊「新增模板」建立第一個 CloudWAPI 訊息模板</p>
          <button
            onClick={openCreate}
            className="inline-flex items-center gap-1 bg-orange-600 text-white px-4 py-2 rounded-lg text-sm hover:bg-orange-700 transition-colors"
          >
            <Plus className="w-4 h-4" /> 新增模板
          </button>
        </div>
      ) : (
        <div className="space-y-8">
          {Array.from(grouped.entries()).map(([category, items]) => (
            <div key={category}>
              {/* Category heading (only shown when viewing "all") */}
              {activeCategory === 'all' && (
                <h2 className="text-lg font-semibold text-gray-700 dark:text-gray-300 mb-3 flex items-center gap-2">
                  <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border ${CATEGORY_BADGE_CLASS[category] || CATEGORY_BADGE_CLASS.general}`}>
                    {CATEGORY_LABELS[category] || category}
                  </span>
                  <span className="text-sm text-gray-400 dark:text-gray-500">({items.length})</span>
                </h2>
              )}

              <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
                {items.map((tpl) => (
                  <div
                    key={tpl.id}
                    className="bg-white dark:bg-gray-800 rounded-lg shadow border border-gray-200 dark:border-gray-700 p-5 flex flex-col gap-3 hover:shadow-md transition-shadow"
                  >
                    {/* Top row: Name + key + badges */}
                    <div className="flex items-start justify-between gap-2">
                      <div className="min-w-0 flex-1">
                        <h3 className="font-bold text-gray-800 dark:text-white truncate">{tpl.name}</h3>
                        <button
                          onClick={() => copyKey(tpl.key)}
                          className="font-mono text-xs text-gray-500 dark:text-gray-400 hover:text-orange-600 dark:hover:text-orange-400 transition-colors inline-flex items-center gap-1"
                          title="點擊複製"
                        >
                          {tpl.key}
                          <Copy className="w-3 h-3" />
                          {copiedKey === tpl.key && (
                            <span className="text-green-500 ml-1">已複製</span>
                          )}
                        </button>
                      </div>
                      <div className="flex items-center gap-1.5 flex-shrink-0">
                        <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium border ${CATEGORY_BADGE_CLASS[tpl.category] || CATEGORY_BADGE_CLASS.general}`}>
                          {CATEGORY_LABELS[tpl.category] || tpl.category}
                        </span>
                        {!toBool(tpl.isActive) && (
                          <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-500 dark:bg-gray-700 dark:text-gray-400">
                            停用
                          </span>
                        )}
                      </div>
                    </div>

                    {/* Used by workflows */}
                    {tpl.usedBy && tpl.usedBy.length > 0 && (
                      <div className="flex flex-wrap gap-1">
                        {tpl.usedBy.map((w) => (
                          <span
                            key={w.workflowKey}
                            className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs bg-blue-50 text-blue-600 border border-blue-200 dark:bg-blue-900/20 dark:text-blue-300 dark:border-blue-800"
                          >
                            📋 {w.workflowName}
                          </span>
                        ))}
                      </div>
                    )}

                    {/* Description */}
                    {tpl.description && (
                      <p className="text-sm text-gray-500 dark:text-gray-400">{tpl.description}</p>
                    )}

                    {/* Message preview */}
                    <div className="bg-gray-50 dark:bg-gray-900 rounded-md p-3 border border-gray-100 dark:border-gray-700">
                      <p className="text-sm text-gray-600 dark:text-gray-300 whitespace-pre-wrap line-clamp-4">
                        {truncate(tpl.message, 120)}
                      </p>
                    </div>

                    {/* Bottom row: media + actions */}
                    <div className="flex items-center justify-between mt-auto pt-1">
                      <div>
                        {tpl.mediaUrl && (
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300">
                            <Paperclip className="w-3 h-3" /> 媒體
                          </span>
                        )}
                      </div>
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => openEdit(tpl)}
                          className="inline-flex items-center gap-1 text-blue-600 hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300 text-xs"
                        >
                          <Edit2 size={14} /> 編輯
                        </button>
                        <button
                          onClick={() => openDeleteConfirm(tpl)}
                          disabled={tpl.usedBy && tpl.usedBy.length > 0}
                          className={`inline-flex items-center gap-1 text-xs ${
                            tpl.usedBy && tpl.usedBy.length > 0
                              ? 'text-gray-400 dark:text-gray-600 cursor-not-allowed'
                              : 'text-red-600 hover:text-red-700 dark:text-red-400 dark:hover:text-red-300'
                          }`}
                          title={tpl.usedBy && tpl.usedBy.length > 0 ? '模板正在使用中，無法刪除' : '刪除模板'}
                        >
                          <Trash2 size={14} /> 刪除
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* ─── Create / Edit Modal ───────────────────────────────────────── */}
      {showModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-xl max-w-2xl w-full max-h-[90vh] flex flex-col">
            {/* Modal header */}
            <div className="p-6 border-b border-gray-200 dark:border-gray-700 flex justify-between items-center">
              <h2 className="text-xl font-bold text-gray-900 dark:text-white">
                {editingId ? '編輯模板' : '新增模板'}
              </h2>
              <button onClick={closeModal} className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200">
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Modal body */}
            <form onSubmit={handleSubmit} className="p-6 overflow-y-auto space-y-5">
              {/* Key (create only) */}
              {!editingId && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    模板代碼 <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    required
                    value={form.key}
                    onChange={(e) => setForm((f) => ({ ...f, key: e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, '') }))}
                    placeholder="例如：supplier_order_notify"
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-orange-500 outline-none bg-white dark:bg-gray-800 dark:text-white font-mono text-sm"
                  />
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">只能包含小寫字母、數字同底線（a-z, 0-9, _）</p>
                </div>
              )}

              {/* Name + Category */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    名稱 <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    required
                    value={form.name}
                    onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                    placeholder="例如：供應商訂單通知"
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-orange-500 outline-none bg-white dark:bg-gray-800 dark:text-white"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">分類</label>
                  <select
                    value={form.category}
                    onChange={(e) => setForm((f) => ({ ...f, category: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-orange-500 outline-none bg-white dark:bg-gray-800 dark:text-white"
                  >
                    {CATEGORIES.filter(c => c.value !== 'all').map((cat) => (
                      <option key={cat.value} value={cat.value}>{cat.label}</option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Description */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">描述</label>
                <input
                  type="text"
                  value={form.description}
                  onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
                  placeholder="簡短描述模板用途"
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-orange-500 outline-none bg-white dark:bg-gray-800 dark:text-white"
                />
              </div>

              {/* Message */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  訊息內容 <span className="text-red-500">*</span>
                </label>
                <textarea
                  required
                  rows={8}
                  value={form.message}
                  onChange={(e) => setForm((f) => ({ ...f, message: e.target.value }))}
                  placeholder="輸入訊息內容，可使用下方變數..."
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-orange-500 outline-none bg-white dark:bg-gray-800 dark:text-white resize-y font-mono text-sm"
                />
                {/* Variable hints */}
                <div className="mt-2 flex flex-wrap gap-1.5">
                  {VARIABLE_HINTS.map((v) => (
                    <button
                      key={v.token}
                      type="button"
                      onClick={() => insertVariable(v.token)}
                      className="inline-flex items-center gap-1 px-2 py-1 rounded text-xs bg-gray-100 text-gray-600 hover:bg-orange-100 hover:text-orange-700 dark:bg-gray-700 dark:text-gray-300 dark:hover:bg-orange-900/30 dark:hover:text-orange-300 transition-colors font-mono"
                      title={v.label}
                    >
                      {v.token}
                    </button>
                  ))}
                </div>
                <div className="mt-1 flex flex-wrap gap-x-3 gap-y-0.5">
                  {VARIABLE_HINTS.map((v) => (
                    <span key={v.token} className="text-xs text-gray-400 dark:text-gray-500">
                      <code className="text-gray-500 dark:text-gray-400">{v.token}</code> — {v.label}
                    </span>
                  ))}
                </div>
              </div>

              {/* Media URL + Type */}
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <div className="sm:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">媒體連結（可選）</label>
                  <input
                    type="url"
                    value={form.mediaUrl}
                    onChange={(e) => setForm((f) => ({ ...f, mediaUrl: e.target.value }))}
                    placeholder="https://example.com/image.jpg"
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-orange-500 outline-none bg-white dark:bg-gray-800 dark:text-white"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">媒體類型</label>
                  <select
                    value={form.mediaType}
                    onChange={(e) => setForm((f) => ({ ...f, mediaType: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-orange-500 outline-none bg-white dark:bg-gray-800 dark:text-white"
                  >
                    {MEDIA_TYPES.map((mt) => (
                      <option key={mt.value} value={mt.value}>{mt.label}</option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Submit */}
              <div className="flex gap-2 pt-2 sticky bottom-0 bg-white dark:bg-gray-800 pb-1">
                <button
                  type="button"
                  onClick={closeModal}
                  className="flex-1 px-4 py-2 bg-gray-200 dark:bg-gray-700 text-gray-800 dark:text-white rounded-lg hover:bg-gray-300 dark:hover:bg-gray-600"
                >
                  取消
                </button>
                <button
                  type="submit"
                  disabled={saving}
                  className="flex-1 px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 disabled:opacity-50 flex items-center justify-center gap-1"
                >
                  {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : editingId ? '儲存變更' : '建立模板'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ─── Delete Confirmation Modal ─────────────────────────────────── */}
      {deleteTarget && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-xl max-w-md w-full">
            <div className="p-6 border-b border-gray-200 dark:border-gray-700 flex justify-between items-center">
              <h2 className="text-lg font-bold text-gray-900 dark:text-white flex items-center gap-2">
                <AlertTriangle className="w-5 h-5 text-red-500" /> 確認刪除
              </h2>
              <button onClick={() => setDeleteTarget(null)} className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200">
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="p-6">
              <p className="text-gray-600 dark:text-gray-300 mb-1">
                確定要刪除模板 <strong className="text-gray-800 dark:text-white">{deleteTarget.name}</strong>（<code className="text-sm bg-gray-100 dark:bg-gray-700 px-1 rounded">{deleteTarget.key}</code>）嗎？
              </p>
              <p className="text-sm text-red-500 dark:text-red-400 mt-2">此操作無法復原。</p>
            </div>
            <div className="p-6 border-t border-gray-200 dark:border-gray-700 flex gap-2">
              <button
                onClick={() => setDeleteTarget(null)}
                className="flex-1 px-4 py-2 bg-gray-200 dark:bg-gray-700 text-gray-800 dark:text-white rounded-lg hover:bg-gray-300 dark:hover:bg-gray-600"
              >
                取消
              </button>
              <button
                onClick={handleDelete}
                disabled={saving}
                className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50 flex items-center justify-center gap-1"
              >
                {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Trash2 className="w-4 h-4" /> 確認刪除</>}
              </button>
            </div>
          </div>
        </div>
      )}
    </AdminLayout>
  );
};

export default AdminCloudwapiTemplates;
